import { defineStore } from 'pinia'
import * as Sentry from '@sentry/vue'
import {
	checkAuth0RedirectCallback,
	getAuth0User,
	isAuth0Authenticated,
	loginAuth0,
	logoutAuth0,
	type CallbackResult,
	type LoginAuth0Options,
} from '@/auth/auth0Client'
import type { LoginResponse } from '@/api/medUsers'

/**
 * Status machine for the global auth state.
 *
 * - `loading`         — initial; the SDK has not yet been polled.
 * - `unauthenticated` — no valid session. Protected routes must redirect to /signin.
 * - `authenticated`   — a valid session is known to exist.
 *
 * Transitions:
 *   loading → authenticated   (init found a session)
 *   loading → unauthenticated (init found no session, or callback failed)
 *   authenticated → unauthenticated (the Auth0 SDK could not mint a token — Auth0SessionExpiredError)
 *   any → loading             (signOut, before redirect completes)
 */
export type AuthStatus = 'loading' | 'unauthenticated' | 'authenticated'

interface AuthState {
	status: AuthStatus
	user: unknown
	/**
	 * Business profile from med-users `authLogin()`. The human-readable name lives HERE
	 * (entered at onboarding), not in `user` — the Auth0 `name` claim defaults to the email
	 * for accounts without a profile name. `null` until login succeeds.
	 */
	profile: LoginResponse | null
	/** Non-fatal error from the last init/callback attempt, surfaced on /signin. */
	initError: string | null
	/** appState.next from the Auth0 callback, consumed by /signin to redirect post-login. */
	pendingNext: string | null
}

export const useAuthStore = defineStore('auth', {
	state: (): AuthState => ({
		status: 'loading',
		user: null,
		profile: null,
		initError: null,
		pendingNext: null,
	}),
	getters: {
		isReady: (s) => s.status !== 'loading',
		isAuthenticated: (s) => s.status === 'authenticated',
		/**
		 * The name to show for the signed-in customer: the med-users profile name ONLY (the real name
		 * entered at onboarding). Deliberately no fall-back to the Auth0 `name` claim — it defaults to the
		 * email for accounts without a profile name, so a screen would read "email · email". `null` until
		 * `setProfile()` and whenever the profile has no name; callers then show just the email.
		 */
		displayName: (s) => s.profile?.name ?? null,
		/** The email to show: the profile's, else the Auth0 `email` claim. */
		displayEmail: (s) => s.profile?.email ?? (s.user as { email?: string } | null)?.email ?? null,
	},
	actions: {
		/**
		 * Run once before app.mount() in main.ts. Handles an Auth0 redirect callback
		 * (if `?code&state` are present) and resolves the initial status.
		 */
		async init() {
			let callback: CallbackResult | null = null
			try {
				callback = await checkAuth0RedirectCallback()
				if (callback?.appState?.next) {
					this.pendingNext = callback.appState.next
				}
				const isAuth = await isAuth0Authenticated()
				if (isAuth) {
					this.user = await getAuth0User()
					this.status = 'authenticated'
				} else {
					this.status = 'unauthenticated'
				}
			} catch (err) {
				// A callback failure (e.g. invalid_state from clock skew, stale link)
				// is non-fatal — the user lands on /signin and can retry. Reported to Sentry: this
				// is an Auth0 SDK exception, not an `authedJson` call, so `captureApiError` (the
				// network capture in src/api/http.ts) never sees it.
				this.initError = err instanceof Error ? err.message : String(err)
				this.status = 'unauthenticated'
				this.user = null
				Sentry.captureException(err, { tags: { flow: 'auth_signin', step: 'redirect_callback' } })
			}
		},

		/** Kick off Auth0 Universal Login. Navigates away; promise never resolves under normal flow. */
		async signIn(options?: LoginAuth0Options) {
			await loginAuth0(options)
		},

		/** Clear local state and trigger Auth0 logout (which redirects back to origin). */
		async signOut() {
			this.status = 'loading'
			this.user = null
			this.profile = null
			this.pendingNext = null
			await logoutAuth0()
		},

		/**
		 * Called by the HTTP transport (`authedJson`) only when the Auth0 SDK cannot mint a
		 * token (`Auth0SessionExpiredError`). A backend 401 is NOT a logout (see
		 * docs/architecture/auth.md). The App.vue watcher reacts to the transition and
		 * redirects to /signin.
		 */
		markUnauthenticated() {
			if (this.status === 'unauthenticated') return
			this.status = 'unauthenticated'
			this.user = null
			this.profile = null
		},

		/** Store the med-users business profile from `authLogin()` (or clear it on failure). */
		setProfile(profile: LoginResponse | null) {
			this.profile = profile
		},

		/** Consume the pending `next` URL captured from the Auth0 callback (if any). */
		takePendingNext(): string | null {
			const next = this.pendingNext
			this.pendingNext = null
			return next
		},
	},
})
